import React, { FunctionComponent, useEffect } from 'react';
import { CircularProgress, Grid, makeStyles } from '@rbx/ui';
import ConfirmDialogProvider from '@modules/ui/dialog/ConfirmDialogProvider';
import SideBarContentProvider from '@modules/ui/shared/components/SideBarContentProvider';
import Navigation from '@modules/utilities/interfaces/Navigation';
import UnifiedTopNavigation from '../../topNavigation/components/UnifiedTopNavigation';
import { htmlThemeAttribute } from '../constants/layoutConstants';
import { useAppConfig } from './AppConfigProvider';

type StyleProps = { isStickyHeader: boolean };

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  header: {
    width: '100%',
    ...(props.isStickyHeader
      ? {
          position: 'sticky',
          top: '0px',
        }
      : {}),
    zIndex: theme.zIndex.appBar,
  },

  // dark theme: it shows nothing since there is no width and height
  // light theme: it shows a light theme fullscreen overlay on top of the content
  lightThemeOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    [`[${htmlThemeAttribute}="light"] &`]: {
      // in https://stackoverflow.com/questions/491052/minimum-and-maximum-value-of-z-index
      // max of zIndex in 2^31 - 1 but 16777271 on Safari 3
      // so 1mil should be safe here
      zIndex: 1000000,
      width: '100vw',
      height: '100vh',
      // hard coded the color here the background color in light theme is not exported.
      backgroundColor: '#F9F9F9',
    },
  },
}));

type AppRootLayoutProps = {
  navigation?: Navigation;
  urlBasePath?: string;
  isStickyHeader?: boolean;
};

const AppRootLayout: FunctionComponent<React.PropsWithChildren<AppRootLayoutProps>> = ({
  children,
  navigation,
  urlBasePath,
  isStickyHeader = false,
}) => {
  const { enableNewLayout, isIXPFetched } = useAppConfig();
  const [isMounted, setIsMounted] = React.useState(false);
  const {
    classes: { lightThemeOverlay },
  } = useStyles({
    isStickyHeader,
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isIXPFetched) {
    return (
      <Grid container alignContent='center' justifyContent='center' height='100vh'>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <ConfirmDialogProvider>
      <SideBarContentProvider navigation={navigation} urlBasePath={urlBasePath}>
        {!isMounted && <div className={lightThemeOverlay} />}
        {!enableNewLayout && <UnifiedTopNavigation />}
        {children}
      </SideBarContentProvider>
    </ConfirmDialogProvider>
  );
};

export default AppRootLayout;
